import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { NOTIFICATION_TRIGGER_KEYS, NotificationDelivery, page } from '@cloudsign/shared';
import type { NotificationRule } from '@cloudsign/shared';
import { Badge, ConfirmButton, EmptyRow, FilterBar, LinkButton, PageHeader, Pager, SelectField, Spinner, TableWrap, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { DateRangePicker, StatusPill, isRangeValid } from '../../components/phase2';
import type { DateRange } from '../../components/phase2';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Tabs, useTab } from '../sites/Tabs';
import { rulesKey, useGroups, useRules, useSites } from './api';

const TABS = [
  { key: 'rules', label: t('ntf.tab.rules') },
  { key: 'log', label: t('ntf.tab.log') },
] as const;

export function NotificationsPage() {
  const [tab, setTab] = useTab(TABS);
  const canManage = useCan('notifications.manage');
  return (
    <div>
      <PageHeader
        title={t('ntf.title')}
        description={t('ntf.description')}
        actions={
          canManage ? (
            <LinkButton to="/manage/notifications/new" variant="primary">
              {t('ntf.rule.add')}
            </LinkButton>
          ) : undefined
        }
      />
      <Tabs tabs={TABS} current={tab} onChange={setTab} label={t('ntf.tabs')}>
        {tab === 'log' ? <DeliveryLog /> : <RulesList />}
      </Tabs>
    </div>
  );
}

function names(ids: string[] | null, all: string, lookup: Map<string, string>): string {
  if (ids === null) return all;
  return ids.map((id) => lookup.get(id) ?? id).join(', ');
}

function recipientsText(r: NotificationRule): string {
  const parts: string[] = [];
  if (r.recipients.host) parts.push(t('ntf.recipient.host'));
  if (r.recipients.member) parts.push(t('ntf.recipient.member'));
  if (r.recipients.reception) parts.push(t('ntf.recipient.reception'));
  if (r.recipients.emails.length > 0) parts.push(t('ntf.recipient.emailsCount', { count: r.recipients.emails.length }));
  if (r.recipients.phones.length > 0) parts.push(t('ntf.recipient.phonesCount', { count: r.recipients.phones.length }));
  if (r.recipients.endpointId) parts.push(t('ntf.recipient.endpointShort'));
  return parts.join(', ');
}

function RulesList() {
  const qc = useQueryClient();
  const rules = useRules();
  const sites = useSites();
  const groups = useGroups();
  const canManage = useCan('notifications.manage');
  const canDelete = useCan('notifications.delete');
  const toggle = useMutation({
    mutationFn: (r: NotificationRule) => api.patch(`/notifications/rules/${r.id}`, { body: { enabled: !r.enabled } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: rulesKey }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/notifications/rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: rulesKey }),
  });
  if (rules.isError) return <ErrorPanel error={rules.error} onRetry={() => void rules.refetch()} />;
  if (!rules.data) return <Spinner />;
  const siteNames = new Map((sites.data ?? []).map((s) => [s.id, s.name]));
  const groupNames = new Map((groups.data ?? []).map((g) => [g.id, g.name]));
  return (
    <>
      <ErrorLine error={toggle.error ?? remove.error} />
      <TableWrap caption={t('ntf.rules.caption')}>
        <thead className="border-b border-slate-200 bg-slate-50">
          <tr>
            <Th>{t('ntf.rule.name')}</Th>
            <Th>{t('ntf.rule.trigger')}</Th>
            <Th>{t('ntf.rules.where')}</Th>
            <Th>{t('ntf.rule.channel')}</Th>
            <Th>{t('ntf.rule.recipients')}</Th>
            <Th>{t('ntf.rules.on')}</Th>
            <Th>
              <span className="sr-only">{t('common.actions')}</span>
            </Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rules.data.length === 0 ? (
            <EmptyRow
              cols={7}
              action={
                canManage ? (
                  <LinkButton to="/manage/notifications/new" variant="primary">
                    {t('ntf.rule.add')}
                  </LinkButton>
                ) : undefined
              }
            />
          ) : null}
          {rules.data.map((r) => (
            <tr key={r.id}>
              <td className="px-4 py-3 font-medium text-slate-900">
                {canManage ? (
                  <Link className="text-brand hover:underline" to={`/manage/notifications/${r.id}`}>
                    {r.name}
                  </Link>
                ) : (
                  r.name
                )}
              </td>
              <td className="px-4 py-3">{t(`notify.trigger.${r.trigger}`)}</td>
              <td className="px-4 py-3 text-slate-600">
                {names(r.siteIds, t('ntf.picker.allSites'), siteNames)}
                <br />
                {names(r.groupIds, t('ntf.picker.allGroups'), groupNames)}
              </td>
              <td className="px-4 py-3">
                <Badge>{t(`ntf.channel.${r.channel}`)}</Badge>
              </td>
              <td className="px-4 py-3 text-slate-600">{recipientsText(r)}</td>
              <td className="px-4 py-3">
                <input
                  type="checkbox"
                  role="switch"
                  className="h-5 w-5 accent-[var(--brand-primary)]"
                  aria-label={t('ntf.rules.switch', { name: r.name })}
                  checked={r.enabled}
                  disabled={!canManage || toggle.isPending}
                  onChange={() => toggle.mutate(r)}
                />
              </td>
              <td className="px-4 py-3">
                {canDelete ? <ConfirmButton label={t('common.delete')} onConfirm={() => remove.mutate(r.id)} pending={remove.isPending} /> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
    </>
  );
}

const DeliveryPage = page(NotificationDelivery);
const STATUSES = ['queued', 'sent', 'logged', 'skipped', 'failed'] as const;
const STATUS_TONE: Record<(typeof STATUSES)[number], 'good' | 'warn' | 'bad' | 'neutral' | 'info'> = {
  queued: 'warn',
  sent: 'good',
  logged: 'info',
  skipped: 'neutral',
  failed: 'bad',
};

function DeliveryLog() {
  const [pageNo, setPageNo] = useState(1);
  const [trigger, setTrigger] = useState('');
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [siteId, setSiteId] = useState('');
  const [range, setRange] = useState<DateRange>({ from: '', to: '' });
  const sites = useSites();
  const valid = isRangeValid(range);
  const query = {
    page: pageNo,
    pageSize: 25,
    trigger: trigger || undefined,
    channel: channel || undefined,
    status: status || undefined,
    siteId: siteId || undefined,
    from: valid && range.from ? new Date(`${range.from}T00:00:00`).toISOString() : undefined,
    to: valid && range.to ? new Date(`${range.to}T23:59:59`).toISOString() : undefined,
  };
  const q = useQuery({
    queryKey: ['notifications', 'deliveries', query],
    placeholderData: keepPreviousData,
    queryFn: () => api.get('/notifications/deliveries', { query, schema: DeliveryPage }),
  });
  const reset = (set: (v: string) => void) => (v: string) => {
    set(v);
    setPageNo(1);
  };
  return (
    <>
      <FilterBar columns="sm:grid-cols-2 lg:grid-cols-4">
        <SelectField label={t('ntf.rule.trigger')} value={trigger} onChange={(e) => reset(setTrigger)(e.target.value)}>
          <option value="">{t('common.all')}</option>
          {NOTIFICATION_TRIGGER_KEYS.map((k) => (
            <option key={k} value={k}>
              {t(`notify.trigger.${k}`)}
            </option>
          ))}
        </SelectField>
        <SelectField label={t('ntf.rule.channel')} value={channel} onChange={(e) => reset(setChannel)(e.target.value)}>
          <option value="">{t('common.all')}</option>
          {(['email', 'sms', 'push', 'webhook'] as const).map((c) => (
            <option key={c} value={c}>
              {t(`ntf.channel.${c}`)}
            </option>
          ))}
        </SelectField>
        <SelectField label={t('common.status')} value={status} onChange={(e) => reset(setStatus)(e.target.value)}>
          <option value="">{t('common.all')}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`ntf.status.${s}`)}
            </option>
          ))}
        </SelectField>
        <SelectField label={t('ntf.log.site')} value={siteId} onChange={(e) => reset(setSiteId)(e.target.value)}>
          <option value="">{t('common.all')}</option>
          {(sites.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <div className="sm:col-span-2 lg:col-span-4">
          <DateRangePicker
            label={t('ntf.log.dates')}
            value={range}
            onChange={(r) => {
              setRange(r);
              setPageNo(1);
            }}
          />
        </div>
      </FilterBar>
      {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      {!q.data && !q.isError ? <Spinner /> : null}
      {q.data ? (
        <>
          <TableWrap caption={t('ntf.log.caption')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('ntf.log.when')}</Th>
                <Th>{t('ntf.rule.trigger')}</Th>
                <Th>{t('ntf.rule.channel')}</Th>
                <Th>{t('ntf.log.to')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('ntf.log.attempts')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.items.length === 0 ? <EmptyRow cols={6} /> : null}
              {q.data.items.map((d) => (
                <tr key={d.id}>
                  <td className="whitespace-nowrap px-4 py-3">{formatDateTime(d.sentAt ?? d.createdAt)}</td>
                  <td className="px-4 py-3">{t(`notify.trigger.${d.trigger}`)}</td>
                  <td className="px-4 py-3">{t(`ntf.channel.${d.channel}`)}</td>
                  <td className="px-4 py-3">
                    <span className="text-slate-500">{t(`ntf.recipientKind.${d.recipientKind}`)}: </span>
                    {d.recipientMasked}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill tone={STATUS_TONE[d.status]}>{t(`ntf.status.${d.status}`)}</StatusPill>
                    {d.errorKey ? <p className="mt-1 text-xs text-slate-600">{t(d.errorKey)}</p> : null}
                  </td>
                  <td className="px-4 py-3">{d.attempts}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={setPageNo} />
        </>
      ) : null}
    </>
  );
}
